<template>
  <q-card style="width: 800px; max-width: 80vw;">
    <q-form @submit.prevent="applyFilters" @reset="reset">
      <q-card-section>
        <div class="text-h6">Фильтры</div>
      </q-card-section>
      <q-card-section>
        <div class="row justify-around items-center">
          <div class="col-11">
            <q-input v-model.trim="filter.searchQuery" label="Поиск" />
          </div>

          <div class="col-12 col-md-5">
            <q-select use-chips multiple v-model="filter.labels" clearable :loading="isLabelLoading" :options="labels"
              option-label="name" option-value="id" label="Ярлыки" emit-value behavior="menu" map-options>
              <template v-slot:no-option>
                <q-item>
                  <q-item-section class="text-italic text-grey">
                    Нет доступных ярлыков
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
          </div>

          <div class="col-12 col-md-5">
            <q-select use-chips multiple v-model="filter.performers" clearable :loading="isUserLoading"
              :options="performerUsers" option-label="username" option-value="email" label="Исполнители" emit-value
              behavior="menu" map-options />
          </div>

          <div class="col-12 col-md-5">
            <q-input v-model="filter.start" label="Старт">
              <template v-slot:prepend>
                <q-icon name="event" class="cursor-pointer">
                  <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                    <q-date v-model="filter.start" mask="YYYY-MM-DD HH:mm">
                      <div class="row items-center justify-end">
                        <q-btn v-close-popup label="Закрыть" color="primary" flat />
                      </div>
                    </q-date>
                  </q-popup-proxy>
                </q-icon>
              </template>

              <template v-slot:append>
                <q-icon name="access_time" class="cursor-pointer">
                  <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                    <q-time v-model="filter.start" mask="YYYY-MM-DD HH:mm" format24h>
                      <div class="row items-center justify-end">
                        <q-btn v-close-popup label="Закрыть" color="primary" flat />
                      </div>
                    </q-time>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>

          <div class="col-12 col-md-5">
            <q-input v-model="filter.end" label="Конец">
              <template v-slot:prepend>
                <q-icon name="event" class="cursor-pointer">
                  <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                    <q-date v-model="filter.end" mask="YYYY-MM-DD HH:mm">
                      <div class="row items-center justify-end">
                        <q-btn v-close-popup label="Закрыть" color="primary" flat />
                      </div>
                    </q-date>
                  </q-popup-proxy>
                </q-icon>
              </template>

              <template v-slot:append>
                <q-icon name="access_time" class="cursor-pointer">
                  <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                    <q-time v-model="filter.end" mask="YYYY-MM-DD HH:mm" format24h>
                      <div class="row items-center justify-end">
                        <q-btn v-close-popup label="Закрыть" color="primary" flat />
                      </div>
                    </q-time>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>

          <div class="col-12 col-md-5">
            <q-select v-model="filter.status" clearable :options="statusOptions" option-label="label" option-value="value"
              label="Статус" emit-value behavior="menu" map-options />
          </div>

          <div class="col-12 col-md-5">
            <q-select v-model="filter.types" multiple use-chips :loading="isTaskTypeLoading" :options="taskTypes"
              option-label="name" option-value="id" label="Тип" emit-value behavior="menu" map-options />
          </div>



          <div class="col-12 col-md-5">
            <q-select v-model="filter.sortBy" clearable :options="taskSortOptions" option-label="label"
              option-value="value" label="Сортировать по" emit-value behavior="menu" map-options />
          </div>
          <div class="col-12 col-md-5">
            <q-select v-model="filter.direction" clearable :options="directionOptions" option-label="label"
              option-value="value" label="Порядок сортировки" emit-value behavior="menu" map-options />
          </div>
          <div class="col-12 col-md-5">
            <q-select v-model.number="filter.size" :options="[
              10, 20, 50
            ]" label="Размер" behavior="menu" />
          </div>
        </div>
      </q-card-section>
      <q-card-actions align="right" class="text-primary">
        <q-btn no-caps flat label="Отмена" color="negative" @click="denyFilters" />
        <q-btn no-caps flat label="Сброс" color="primary" type="reset" />
        <q-btn no-caps label="Применить" color="primary" type="submit" />
      </q-card-actions>
    </q-form>

  </q-card>
</template>

<script setup lang="ts">
import { taskSortOptions, directionOptions } from "../util/sort";
import { statusOptions } from "../util/state";
import { useTaskStore } from "../store/task";
import { storeToRefs } from "pinia";
import { computed, reactive } from "vue";
import { useRoute } from "vue-router";
import { TaskFilter } from "../models/task";
import { useProjectLabels, useProjectUsers } from "../composables/project";
import { useTaskTypes } from "../composables/task";

const emit = defineEmits<{
  (e: 'apply'): void
  (e: 'cancel'): void
}>()
const projectId = computed(() => Number(route.params.projectId))
const route = useRoute()

const store = useTaskStore()
const { filters } = storeToRefs(store)

const { data: labels, isFetching: isLabelLoading } = useProjectLabels(projectId)
const { data: users, isFetching: isUserLoading } = useProjectUsers(projectId)
const { data: taskTypes, isFetching: isTaskTypeLoading } = useTaskTypes(projectId)

const performerUsers = computed(() => users.value?.filter(u => u.permission !== "READ").map(u => u.user) ?? [])

const filter = reactive<TaskFilter>(filters.value.get(projectId.value) ?? {
  searchQuery: null,
  sortBy: "priority",
  direction: "ASC",
  performers: [],
  labels: [],
  types: [],
  start: null,
  end: null,
  size: 50,
  status: null
})

async function reset() {
  filter.searchQuery = null
  filter.start = null
  filter.sortBy = "priority"
  filter.direction = "ASC"
  filter.end = null
  filter.size = 10
  filter.types = []
  filter.status = null
  filter.performers = []
  filter.labels = []
}

async function applyFilters() {
  filters.value.set(projectId.value, filter)
  await store.execute()
  emit('apply')
}

function denyFilters() {
  emit('cancel')

}
</script>

<style scoped></style>